import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { StatusBadge } from '@/components/ui/status-badge';
import { ApiError, authApi, legalDocumentsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { buildLegalDocumentIframeSrcDoc } from './legal-document-rendering';
import { LegalDocumentAssignmentEditor } from './LegalDocumentAssignmentEditor';
import { LegalDocumentAssignmentsListPage } from './LegalDocumentAssignmentsListPage';
import { LegalDocumentAcceptancesList } from './LegalDocumentAcceptancesList';
import { LegalDocumentEditor } from './LegalDocumentEditor';
import { LegalAgreementImpactConfirmation } from './LegalAgreementImpactConfirmation';
import type {
    ActiveRoleAssignmentResponse,
    GetLegalDocumentResponse,
    LegalDocumentMutableStatus,
} from '@pillar-portal/api-types';
import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    hasInternalAdminPrivileges,
} from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import { formatLegalDocumentPurpose } from './legal-document-purpose-format';

/**
 * The server's own explanation, never the transport fallback, which can carry
 * a raw HTTP status or response body (BUG-261).
 */
function legalDocumentActionError(error: unknown, fallback: string): string {
    const serverMessage = error instanceof ApiError ? error.responseMessage : undefined;
    return serverMessage ?? fallback;
}

export function LegalDocumentDetailPage() {
    const log = createComponentLogger('LegalDocumentDetailPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [confirmingRevokeAll, setConfirmingRevokeAll] = useState(false);
    const [revokeAllNotice, setRevokeAllNotice] = useState<string | null>(null);
    const params = useParams();
    const legalDocumentUuidParam: string | undefined = params.id;
    const legalDocumentUuid: string = useMemo(() => {
        if (legalDocumentUuidParam === undefined) {
            throw new Error('Missing legal document UUID');
        }
        const parseResult = z.string().uuid().safeParse(legalDocumentUuidParam);
        if (!parseResult.success) {
            throw new Error('Invalid legal document UUID');
        }
        return parseResult.data;
    }, [legalDocumentUuidParam]);

    const { data, isLoading, error } = useQuery<GetLegalDocumentResponse>({
        queryKey: ['legalDocument', legalDocumentUuid],
        queryFn: async () => {
            return await legalDocumentsApi.getLegalDocument(legalDocumentUuid);
        },
        staleTime: 0,
    });
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });
    const canManageLifecycle: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment?.activation_state === 'operational' ? activeRoleAssignment : null
    );
    const [pendingStatus, setPendingStatus] = useState<LegalDocumentMutableStatus | null>(null);
    const updateStatusMutation = useMutation({
        mutationFn: async (status: LegalDocumentMutableStatus) => {
            return await legalDocumentsApi.updateLegalDocumentStatus(legalDocumentUuid, { status });
        },
        onSuccess: async () => {
            setMutationError(null);
            setPendingStatus(null);
            await queryClient.invalidateQueries({ queryKey: ['legalDocument', legalDocumentUuid] });
            await queryClient.invalidateQueries({ queryKey: ['legalDocuments'] });
        },
        onError: (mutationErr: unknown) => {
            setPendingStatus(null);
            log.error('Failed to update legal document status', mutationErr);
            setMutationError('Unable to update legal document status.');
        },
    });

    const undeleteMutation = useMutation({
        mutationFn: async () => await legalDocumentsApi.undeleteLegalDocument(legalDocumentUuid),
        onSuccess: async (restored: GetLegalDocumentResponse) => {
            setMutationError(null);
            queryClient.setQueryData<GetLegalDocumentResponse>(
                ['legalDocument', legalDocumentUuid],
                restored
            );
            await queryClient.invalidateQueries({ queryKey: ['legalDocuments'] });
        },
        onError: (mutationErr: unknown) => {
            log.error('Failed to undelete legal document', mutationErr);
            setMutationError(
                'Unable to undelete the legal document. Reload the page and try again.'
            );
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () => await legalDocumentsApi.deleteLegalDocument(legalDocumentUuid),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['legalDocuments'] });
            breadcrumbNavigate({
                source: 'panel',
                target: { url: '/legal-documents', title: 'Legal Documents' },
            });
        },
        onError: (mutationErr: unknown) => {
            setConfirmingDelete(false);
            setRevokeAllNotice(null);
            setMutationError(
                legalDocumentActionError(mutationErr, 'Unable to delete the legal document.')
            );
        },
    });

    const revokeAllMutation = useMutation({
        mutationFn: async () =>
            await legalDocumentsApi.revokeAllLegalDocumentAssignments(legalDocumentUuid),
        onSuccess: async (result) => {
            setConfirmingRevokeAll(false);
            setMutationError(null);
            setRevokeAllNotice(
                result.revoked_assignment_count === 1
                    ? 'Revoked 1 assignment. The document can now be deleted.'
                    : `Revoked ${result.revoked_assignment_count} assignments. The document can now be deleted.`
            );
            await queryClient.invalidateQueries({ queryKey: ['legalDocumentAssignments'] });
            await queryClient.invalidateQueries({ queryKey: ['organizationAgreements'] });
        },
        onError: (mutationErr: unknown) => {
            setConfirmingRevokeAll(false);
            setRevokeAllNotice(null);
            log.error('Failed to revoke all legal document assignments', mutationErr);
            setMutationError(
                legalDocumentActionError(mutationErr, 'Unable to revoke the assignments.')
            );
        },
    });

    if (isLoading) {
        return (
            <div className="p-6">
                <p>Loading...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6">
                <p>Error loading legal document.</p>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="p-6">
                <p>Missing legal document data.</p>
            </div>
        );
    }

    const doc = data.legal_document;
    // A deleted document is read-only apart from Undelete (FEAT-267).
    const isDeleted: boolean = doc.status === Status.DELETED;
    const canChangeDocument: boolean = canManageLifecycle && !isDeleted;
    const srcDoc: string = buildLegalDocumentIframeSrcDoc({
        legalDocumentContentType: doc.legal_document_content_type,
        legalDocumentText: doc.legal_document_text,
    });

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <LegalAgreementImpactConfirmation
                request={
                    pendingStatus === null
                        ? null
                        : {
                              action: 'change_status',
                              legal_document_uuid: legalDocumentUuid,
                              status: pendingStatus,
                          }
                }
                title={
                    pendingStatus === Status.DISABLED
                        ? 'Disable this document?'
                        : 'Activate this document?'
                }
                confirmLabel={pendingStatus === Status.DISABLED ? 'Disable' : 'Activate'}
                isConfirming={updateStatusMutation.isPending}
                confirmBlocked={(impact) => impact.leaves_no_platform_default}
                onConfirm={() => {
                    if (pendingStatus !== null) {
                        updateStatusMutation.mutate(pendingStatus);
                    }
                }}
                onCancel={() => setPendingStatus(null)}
            >
                {(impact) =>
                    impact.leaves_no_platform_default ? (
                        <p>Assign another platform default before disabling this document.</p>
                    ) : null
                }
            </LegalAgreementImpactConfirmation>
            <ConfirmationModal
                open={confirmingRevokeAll}
                onOpenChange={setConfirmingRevokeAll}
                title="Revoke all assignments?"
                message="Every active assignment of this document ends now, for every Organization and as the platform default. Acceptances stay on record."
                confirmLabel="Revoke all"
                isLoading={revokeAllMutation.isPending}
                onConfirm={() => revokeAllMutation.mutate()}
            />
            {mutationError === null ? null : (
                <div
                    role="alert"
                    className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                    {mutationError}
                </div>
            )}
            {revokeAllNotice === null ? null : (
                <div
                    role="status"
                    className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
                >
                    {revokeAllNotice}
                </div>
            )}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold">{doc.legal_document_title}</h1>
                        <StatusBadge status={doc.status} />
                    </div>
                    <p className="text-sm text-gray-600">Version: {doc.legal_document_version}</p>
                    <p className="text-sm text-gray-600">
                        Purpose: {formatLegalDocumentPurpose(doc.legal_document_purpose)}
                    </p>
                    <p className="text-sm text-gray-600">
                        Acceptance validity:{' '}
                        {doc.acceptance_validity_duration_days === null
                            ? doc.legal_document_purpose === 'ORGANIZATION_AGREEMENT'
                                ? 'Indefinite'
                                : 'Not applicable'
                            : `${doc.acceptance_validity_duration_days} days`}
                    </p>
                    <p className="text-sm text-gray-600">Digest: {doc.content_sha256}</p>
                    <p className="text-sm text-gray-600">
                        In force:{' '}
                        {formatTimestampForMerchantUi(doc.in_force_start, SYSTEM_DISPLAY_TIME_ZONE)}{' '}
                        -{' '}
                        {doc.in_force_end === null
                            ? 'No end date'
                            : formatTimestampForMerchantUi(
                                  doc.in_force_end,
                                  SYSTEM_DISPLAY_TIME_ZONE
                              )}
                    </p>
                </div>
                <div className="flex gap-2">
                    {canManageLifecycle && isDeleted ? (
                        <Button
                            variant="outline"
                            disabled={undeleteMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                undeleteMutation.mutate();
                            }}
                        >
                            Undelete
                        </Button>
                    ) : null}
                    {canChangeDocument && !isEditing ? (
                        <Button variant="outline" onClick={() => setIsEditing(true)}>
                            Edit
                        </Button>
                    ) : null}
                    {canChangeDocument && !confirmingDelete ? (
                        <Button
                            variant="outline"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                setConfirmingDelete(true);
                            }}
                        >
                            Delete
                        </Button>
                    ) : null}
                    {canChangeDocument ? (
                        <Button
                            variant="outline"
                            disabled={revokeAllMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                setRevokeAllNotice(null);
                                setConfirmingRevokeAll(true);
                            }}
                        >
                            Revoke all
                        </Button>
                    ) : null}
                    {canChangeDocument && confirmingDelete ? (
                        <>
                            <Button
                                variant="destructive"
                                disabled={deleteMutation.isPending}
                                onClick={() => deleteMutation.mutate()}
                            >
                                Confirm delete
                            </Button>
                            <Button variant="outline" onClick={() => setConfirmingDelete(false)}>
                                Keep
                            </Button>
                        </>
                    ) : null}
                    {canManageLifecycle && doc.status === Status.ACTIVE ? (
                        <Button
                            variant="outline"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                setPendingStatus(Status.DISABLED);
                            }}
                        >
                            Disable
                        </Button>
                    ) : null}
                    {canManageLifecycle &&
                    (doc.status === Status.PENDING || doc.status === Status.DISABLED) ? (
                        <Button
                            variant="outline"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                setPendingStatus(Status.ACTIVE);
                            }}
                        >
                            {doc.status === Status.PENDING ? 'Activate' : 'Re-enable'}
                        </Button>
                    ) : null}
                    <Button
                        variant="outline"
                        onClick={() => {
                            runInBackground(
                                Promise.resolve(
                                    breadcrumbNavigate({
                                        source: 'panel',
                                        target: {
                                            url: '/legal-documents',
                                            title: 'Legal Documents',
                                        },
                                    })
                                ),
                                (err: unknown) => {
                                    log.error('Navigation failed', err);
                                }
                            );
                        }}
                    >
                        Back
                    </Button>
                </div>
            </div>

            {isDeleted ? (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    This document is deleted. Undelete brings it back as Disabled, with no
                    assignments.
                </div>
            ) : null}

            {isEditing ? (
                <div className="mb-6">
                    <LegalDocumentEditor
                        legalDocument={doc}
                        onCancel={() => setIsEditing(false)}
                        onSaved={(saved) => {
                            setIsEditing(false);
                            queryClient.setQueryData<GetLegalDocumentResponse>(
                                ['legalDocument', legalDocumentUuid],
                                { legal_document: saved }
                            );
                            runInBackground(
                                queryClient.invalidateQueries({ queryKey: ['legalDocuments'] }),
                                (refreshError: unknown) =>
                                    log.error('Failed to refresh legal documents', refreshError)
                            );
                            // An in-place edit can change whether each acceptance matches
                            // the current text.
                            runInBackground(
                                queryClient.invalidateQueries({
                                    queryKey: ['legalDocumentAcceptances', legalDocumentUuid],
                                }),
                                (refreshError: unknown) =>
                                    log.error(
                                        'Failed to refresh legal document acceptances',
                                        refreshError
                                    )
                            );
                        }}
                    />
                </div>
            ) : null}

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Document</CardTitle>
                </CardHeader>
                <CardContent>
                    <iframe
                        title="Legal Document"
                        sandbox="allow-scripts"
                        className="w-full h-[70vh] border rounded"
                        srcDoc={srcDoc}
                    />
                </CardContent>
            </Card>

            {canManageLifecycle ? (
                <div className="mt-6 space-y-6">
                    {isDeleted ? null : (
                        <LegalDocumentAssignmentEditor
                            legalDocument={doc}
                            onAssigned={() => {
                                runInBackground(
                                    queryClient.invalidateQueries({
                                        queryKey: ['legalDocumentAssignments'],
                                    }),
                                    (assignmentError: unknown) => {
                                        log.error(
                                            'Failed to refresh legal document assignments',
                                            assignmentError
                                        );
                                    }
                                );
                            }}
                        />
                    )}
                    <LegalDocumentAssignmentsListPage
                        initialQueryText={`${doc.legal_document_title} ${doc.legal_document_version}`}
                        legalDocumentUuid={doc.legal_document_uuid}
                    />
                    <LegalDocumentAcceptancesList legalDocumentUuid={doc.legal_document_uuid} />
                </div>
            ) : null}
        </div>
    );
}
